import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { collection, query, where, orderBy, limit, limitToLast, startAfter, endBefore, onSnapshot, getDocs, type Firestore, type QueryDocumentSnapshot } from 'firebase/firestore';
import { EventWindow, type EventPage } from '../utils/eventWindow';
import { EVENT_PAGE_SIZE, EVENT_WINDOW_PAGES, normalizeFeedEvent, publishedEvent, matchesFeed } from '../utils/riskEventFeed';
import type { RiskEvent } from '@types';

interface Props {
    database: Firestore;
    scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
    country?: string | null;
    critical: boolean;
    category: string;
    renderEvent: (event: RiskEvent) => React.ReactNode;
    onEvents: (events: RiskEvent[]) => void;
}

export default function PagedRiskEventFeed({ database, country, critical, category, renderEvent, onEvents, scrollContainerRef }: Props) {
    const [, redraw] = useState(0);
    const [restart, setRestart] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const controller = useRef<EventWindow<RiskEvent, QueryDocumentSnapshot> | null>(null);
    const localScroll = useRef<HTMLDivElement>(null);
    const scroll = scrollContainerRef || localScroll;
    const anchor = useRef<{ id: string; top: number } | null>(null);
    const onEventsRef = useRef(onEvents);
    onEventsRef.current = onEvents;

    useEffect(() => {
        let disposed = false;
        setLoading(true);
        setError('');
        onEventsRef.current([]);
        if (scroll.current)
            scroll.current.scrollTop = 0;
        const base = query(collection(database, 'risk_events'),
            ...(country ? [where('geo.country_iso3', '==', country)] : []), orderBy('processedAt', 'desc'));
        const toPage = (docs: QueryDocumentSnapshot[]): EventPage<RiskEvent, QueryDocumentSnapshot> => ({
            rows: docs.filter(doc => publishedEvent(doc.data())).map(doc => normalizeFeedEvent(doc.id, doc.data())),
            first: docs[0], last: docs.at(-1), full: docs.length === EVENT_PAGE_SIZE,
        });
        const window = new EventWindow<RiskEvent, QueryDocumentSnapshot>(async (direction, cursor) => {
            const page = direction === 'next' ? query(base, startAfter(cursor), limit(EVENT_PAGE_SIZE))
                : query(base, endBefore(cursor), limitToLast(EVENT_PAGE_SIZE));
            return toPage((await getDocs(page)).docs);
        }, () => {
            if (disposed)
                return;
            onEventsRef.current(window.pages.flatMap(page => page.rows));
            redraw(value => value + 1);
        }, EVENT_WINDOW_PAGES);
        controller.current = window;
        const unsubscribe = onSnapshot(query(base, limit(EVENT_PAGE_SIZE)), snapshot => {
            if (disposed)
                return;
            window.latest(toPage(snapshot.docs));
            setLoading(false);
        }, () => { if (!disposed) { setLoading(false); setError('Could not load events. Please retry.'); } });
        return () => { disposed = true; unsubscribe(); window.dispose(); };
    }, [database, country, critical, category, restart]);

    // Preserve the visible row when an old page leaves the sliding window.
    useLayoutEffect(() => {
        const root = scroll.current;
        if (!root || !anchor.current || controller.current?.busy)
            return;
        const row = Array.from(root.querySelectorAll<HTMLElement>('[data-event-id]')).find(row => row.dataset.eventId === anchor.current!.id);
        if (row)
            root.scrollTop += row.getBoundingClientRect().top - anchor.current.top;
        anchor.current = null;
    });

    const move = async (direction: 'next' | 'previous') => {
        const root = scroll.current;
        const window = controller.current;
        if (!root || !window || window.busy)
            return;
        const row = Array.from(root.querySelectorAll<HTMLElement>('[data-event-id]')).find(row => row.getBoundingClientRect().bottom > root.getBoundingClientRect().top);
        if (row)
            anchor.current = { id: row.dataset.eventId!, top: row.getBoundingClientRect().top };
        await window.move(direction);
    };
    useEffect(() => {
        const root = scroll.current;
        if (!root)
            return;
        const loadNearBottom = () => {
            if (root.scrollTop + root.clientHeight >= root.scrollHeight - 120)
                void move('next');
        };
        root.addEventListener('scroll', loadNearBottom, { passive: true });
        return () => root.removeEventListener('scroll', loadNearBottom);
    }, [scroll]);
    const window = controller.current;
    const rows = [...new Map((window?.pages.flatMap(page => page.rows) || []).map(event => [event.id, event])).values()]
        .filter(event => matchesFeed(event, critical, category));
    return <section className="bg-white" aria-label="Intelligence events">
        <div className="flex items-center justify-between border-b p-2 text-xs text-slate-500">
            <span>{window?.offset || (window?.pages.length || 0) > 1 ? 'Browsing history' : 'Latest events'} · {rows.length} loaded</span>
            <button className="font-semibold text-indigo-700" onClick={() => setRestart(value => value + 1)}>{window?.newer ? 'New events · Back to latest' : 'Back to latest'}</button>
        </div>
        <div ref={localScroll} className={scrollContainerRef ? undefined : "h-[420px] overflow-y-auto overscroll-contain"} tabIndex={scrollContainerRef ? undefined : 0}>
            {!!window?.offset && <button className="w-full p-3 text-sm text-indigo-700" disabled={window.busy} onClick={() => void move('previous')}>Load newer events</button>}
            {loading ? <p role="status" className="p-6 text-sm text-slate-500">Loading events…</p> : rows.map(event => <div key={event.id} data-event-id={event.id} style={{ contentVisibility: 'auto', containIntrinsicSize: 'auto 180px' }}>{renderEvent(event)}</div>)}
            {!loading && !rows.length && !error && <p className="p-6 text-sm text-slate-500">No matching events in these pages.</p>}
            {(error || window?.error) && <p role="alert" className="p-3 text-sm text-red-700">{error || window?.error}</p>}
            {!loading && <button className="w-full p-3 text-sm font-semibold text-indigo-700 disabled:text-slate-400" disabled={window?.busy || (!error && !window?.older)} onClick={() => error ? setRestart(value => value + 1) : void move('next')}>
                {window?.busy ? 'Loading…' : error || window?.error ? 'Retry loading events' : window?.older ? 'Load 10 more' : 'End of feed'}
            </button>}
        </div>
    </section>;
}
